import { create } from "zustand"
import { persist } from "zustand/middleware"

import { IOrganization } from "~/types/pre-meeting"

interface IuseMarketingOsStore {
  quickSearch: boolean
  setQuickSearch: (value: boolean) => void
  showSidebar: boolean
  setShowSidebar: (value: boolean) => void
  // Ephemeral: whether the Outbound agent view is shown in marketingOS.
  // Intentionally NOT persisted — resets to true on every fresh load so users
  // always return to the Outbound view by default.
  outboundViewEnabled: boolean
  setOutboundViewEnabled: (value: boolean) => void
  // Which module the user entered the outbound agent from: the MOS "new view"
  // toggle (prospectos) or the /agent-os/outbound route (agentos). The outbound
  // sidebar's first tab returns to THIS module instead of always the agent-os
  // route.
  outboundEntryOrigin: "prospectos" | "agentos"
  setOutboundEntryOrigin: (value: "prospectos" | "agentos") => void
  searchValue: string
  setSearchValue: (value: string) => void
  searchLoading: boolean
  setSearchLoading: (value: boolean) => void
  callerModal: any
  setCallerModal: (value: any) => void
  resetCallerModal: () => void
  activeAccount: IOrganization | null
  setActiveAccount: (value: IOrganization | null) => void
  activePerson: any
  setActivePerson: (value: any) => void
  openMessageTemplate: any
  setOpenMessageTemplate: (value: any) => void
  emailsFetched: Map<string, string>
  setEmailsFetched: (key: string, value: any) => void
  emailsLoading: Map<string, boolean>
  setEmailsLoading: (key: string, value: boolean) => void
  phoneNumbersFetched: Map<string, string[]>
  setPhoneNumbersFetched: (key: string, value: any) => void
  phoneNumbersLoading: Map<string, boolean>
  setPhoneNumbersLoading: (key: string, value: boolean) => void
  exclusionInfoFetched: Map<
    string,
    { is_exclusion: boolean; exclusion_reason: string | null }
  >
  setExclusionInfoFetched: (
    key: string,
    value: { is_exclusion: boolean; exclusion_reason: string | null }
  ) => void
  clearMaps: () => void
  activeOpportunityId: string
  setActiveOpportunityId: (value: string) => void
}

const CALLER_INITIAL_STATE = {
  dialerStatus: "IDLE",
  callStatus: "IDLE"
}

const useMarketingOsStore = create<IuseMarketingOsStore>()(
  persist(
    (set) => ({
      quickSearch: false,
      setQuickSearch: (value) => set({ quickSearch: value }),
      showSidebar: true,
      setShowSidebar: (value) => set({ showSidebar: value }),
      outboundViewEnabled: true,
      setOutboundViewEnabled: (value) => set({ outboundViewEnabled: value }),
      outboundEntryOrigin: "agentos",
      setOutboundEntryOrigin: (value) => set({ outboundEntryOrigin: value }),
      searchValue: "",
      setSearchValue: (value) => set({ searchValue: value }),
      searchLoading: false,
      setSearchLoading: (value) => set({ searchLoading: value }),
      callerModal: CALLER_INITIAL_STATE,
      setCallerModal: (value) =>
        set((state) => ({
          callerModal: { ...state.callerModal, ...value }
        })),
      resetCallerModal: () =>
        set(() => ({
          callerModal: CALLER_INITIAL_STATE
        })),
      activeAccount: null,
      setActiveAccount: (value) => set({ activeAccount: value }),
      activePerson: null,
      setActivePerson: (value) => set({ activePerson: value }),
      openMessageTemplate: null,
      setOpenMessageTemplate: (value) => set({ openMessageTemplate: value }),
      emailsFetched: new Map(),
      setEmailsFetched: (key: string, val: any) =>
        set((store) => ({
          emailsFetched: new Map(store.emailsFetched.set(key, val))
        })),
      emailsLoading: new Map(),
      setEmailsLoading: (key: string, val: boolean) =>
        set((store) => ({
          emailsLoading: new Map(store.emailsLoading.set(key, val))
        })),
      phoneNumbersFetched: new Map(),
      setPhoneNumbersFetched: (key: string, val: any) =>
        set((store) => ({
          phoneNumbersFetched: new Map(store.phoneNumbersFetched.set(key, val))
        })),
      phoneNumbersLoading: new Map(),
      setPhoneNumbersLoading: (key: string, val: boolean) =>
        set((store) => ({
          phoneNumbersLoading: new Map(store.phoneNumbersLoading.set(key, val))
        })),
      exclusionInfoFetched: new Map(),
      setExclusionInfoFetched: (key: string, val) =>
        set((store) => ({
          exclusionInfoFetched: new Map(
            store.exclusionInfoFetched.set(key, val)
          )
        })),
      clearMaps: () =>
        set(() => ({
          emailsFetched: new Map(),
          emailsLoading: new Map(),
          phoneNumbersFetched: new Map(),
          phoneNumbersLoading: new Map(),
          exclusionInfoFetched: new Map()
        })),
      activeOpportunityId: "",
      setActiveOpportunityId: (value) => set({ activeOpportunityId: value })
    }),
    {
      // Persist ONLY the selected outbound module so a refresh keeps the user in
      // prospectOS vs agentOS instead of defaulting back to agentOS.
      name: "marketing-os-store",
      partialize: (state) => ({
        outboundEntryOrigin: state.outboundEntryOrigin
      })
    }
  )
)

export default useMarketingOsStore
